import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import Select from 'react-select';
import ReactSlider from 'react-slider';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import FormLabel from '@/Component/Common/Forms/FormLabel';
import FormikInput from '@/Component/Common/Forms/FormikInput';
import { useQuery } from '@tanstack/react-query';
import { CategoryAPI, CategoryKeywordAPI, CountriesAPI } from '@/Utils/APIRequest/Endpoints';
import CustomFetch from '@/Utils/APIRequest/AxiosInstance';
import BorderInputWithCommas from '@/Component/Common/Forms/BorderInputWithCommas';
import { useTranslation } from '@/Utils/Hooks/useTranslate';
// import categoriesData from '@/locales/categories.json'; // Adjust the path as necessary

const SearchForm = (props) => {
    const { setFilteredValue, handleDropdown, filteredValue, setMainParams, translations } = props;
    const pathName = usePathname();
    const router = useRouter();
    const [params, setParams] = useState({ page: 1, per_page: 50 });
    const { locale } = useTranslation();

    // Topics Get API
    const { data: topicsData, refetch } = useQuery({ queryKey: [CategoryAPI, params, locale], queryFn: () => CustomFetch({ url: CategoryAPI, params: { ...params } }, null, false), refetchOnWindowFocus: false, select: (data) => data?.data?.data });

    // Topic Options
    const topicOptions = topicsData?.filter(skill => skill != null && !skill.is_hidden)?.map((skill) => ({
        label: skill.translations?.[0]?.title,
        value: skill.id,
    }));

    // Platform Options
    const platformOptions: any = [
        { value: 'instagram', label: 'Instagram' },
        { value: 'facebook', label: 'Facebook' },
        { value: 'youtube', label: 'YouTube' },
        { value: 'tiktok', label: 'TikTok' },
        { value: 'xiaohongshu', label: 'Xiaohongshu' }
    ];

    // Get Country API
    const { data: CountryData, isPending: CountryPending } = useQuery({ queryKey: [CountriesAPI], queryFn: () => CustomFetch({ url: `${CountriesAPI}?search=` }, null, false), refetchOnWindowFocus: false, select: (data) => data?.data?.data });

    const countryOptions = CountryData?.map((country) => ({
        label: country?.country_name,
        value: country?.two_digit_code
    }));

    // Topics Keywords API
    const { data: categoriesData, refetch: refetchCategories } = useQuery({ queryKey: [CategoryKeywordAPI, params], queryFn: () => CustomFetch({ url: CategoryKeywordAPI, params: { ...params } }, null, false), refetchOnWindowFocus: false, select: (data) => data?.data });

    // const topicKeywordMap = Object?.entries(categoriesData?.categories).reduce((acc, [categoryName, keywords]) => {
    //     acc[categoryName] = keywords?.map((k) => ({
    //         label: k.keyword,
    //         value: k.id,
    //     }));
    //     return acc;
    // }, {});

    const topicKeywordMap =
        categoriesData?.categories
            ? Object.entries(categoriesData.categories).reduce(
                (acc: any, [categoryName, keywords]) => {
                    const keywordArray: any = Array.isArray(keywords) ? keywords : [];
                    acc[categoryName] = keywordArray.map((k) => ({
                        label: k.keyword,
                        value: k.id,
                    }));
                    return acc;
                },
                {}
            )
            : {};

    return (
        <Formik
            initialValues={{
                country: [],
                gender: '',
                platform: [],
                minFollower: 2000,
                maxFollower: 1000000,
                minEngagement: 0.0,
                maxEngagement: 10.0,
                topic: [],
                keywords: [],
            }}
            onSubmit={(values) => {
                const formattedValues = {
                    platforms: values.platform,
                    skills: values.topic,
                    country: values.country,
                    gender: values.gender,
                    minFollower: values.minFollower,
                    maxFollower: values.maxFollower,
                    minEngagement: values.minEngagement,
                    maxEngagement: values.maxEngagement,
                    keywords: values.keywords,
                };
                setMainParams((prev) => ({ ...prev, page: 1 }));
                handleDropdown();
                setFilteredValue(formattedValues);
                // if (pathName.includes('browse-influencer')) {
                //     router.push('/brand/browse-influencer/search');
                // }
            }}
        >
            {({ values, setFieldValue }) => {
                useEffect(() => {
                    if (filteredValue) {
                        console.log(filteredValue);
                        const countrySelectedOptions = countryOptions?.filter(option =>
                            filteredValue?.country?.some(selectedCountry => selectedCountry.value === option.value)
                        );
                        setFieldValue('country', countrySelectedOptions || []);

                        const topicSelectedOptions = topicOptions?.filter(option =>
                            filteredValue?.skills?.some(selectedTopic => selectedTopic.value === option.value)
                        );
                        setFieldValue('topic', (topicSelectedOptions || []));

                        const selectedKeyword = Object.values(topicKeywordMap)?.filter((option: any) =>
                            filteredValue?.skills?.some(selectedKeyword => selectedKeyword.value === option.value)
                        );
                        setFieldValue('keywords', (selectedKeyword || []));

                        const platformSelectedOptions = platformOptions?.filter(option =>
                            filteredValue?.platforms?.some(selectedPlatform => selectedPlatform.value === option.value)
                        );
                        setFieldValue('platform', (platformSelectedOptions || []));

                        const matchedKeywords = Object.values(topicKeywordMap)
                            .flat() // Flatten the array of keyword arrays
                            .filter((keyword: any) =>
                                filteredValue?.keywords?.some((selected: any) => selected.value === keyword.value)
                            );

                        setFieldValue('keywords', matchedKeywords || []);

                        setFieldValue('gender', (filteredValue?.gender || ''));
                        setFieldValue('minFollower', (filteredValue?.minFollower || 2000));
                        setFieldValue('maxFollower', (filteredValue?.maxFollower || 1000000));
                        setFieldValue('minEngagement', (filteredValue?.minEngagement || 0.0));
                        setFieldValue('maxEngagement', (filteredValue?.maxEngagement || 10.0));
                    }

                    if (filteredValue?.keywords?.length) {
                        const selectedKeyword = Object.values(topicKeywordMap)
                            .flat()
                            .find((k: any) => k.value === filteredValue?.keywords?.[0]?.value);
                        setFieldValue('keywords', selectedKeyword || null);
                    }
                }, [filteredValue, setFieldValue]);

                const selectedTopicLabels = values.topic?.map((t: any) => t.label) || [];
                const matchedKeywords = selectedTopicLabels.flatMap(label => topicKeywordMap[label] || []);
                return (
                    <Form className="space-y-5">
                        {/* Country Multi-Select */}
                        <div className='grid md:grid-cols-12 gap-3 items-center'>
                            <FormLabel customClass="col-span-3 !mb-0" title={translations?.country} />
                            <div className='col-span-9'>
                                <Select
                                    isMulti
                                    name="country"
                                    options={countryOptions}
                                    className="basic-multi-select"
                                    classNamePrefix="custom-select"
                                    placeholder={translations?.countryPlaceholder}
                                    value={values.country}
                                    onChange={(selectedOptions) => {
                                        setFieldValue('country', selectedOptions);
                                    }}
                                />
                            </div>
                        </div>

                        {/* Gender Radio Buttons */}
                        {/* <div className='grid md:grid-cols-12 gap-3 items-center'>
                            <FormLabel customClass="col-span-3 !mb-0" title={translations?.gender} />
                            <div className="flex items-center col-span-9 gap-3">
                                <label htmlFor='male' className="flex-1 relative cursor-pointer">
                                    <Field type="checkbox" id="male" name="gender" value="male" className="hidden peer" />
                                    <span className='peer-checked:bg-gradient-to-br from-secondary to-primary peer-checked:text-white peer-checked:font-medium text-center border border-gray rounded-xl w-full block px-2 py-2.5'>{translations?.male}</span>
                                </label>
                                <label htmlFor='female' className="flex-1 relative cursor-pointer">
                                    <Field type="checkbox" id="female" name="gender" value="female" className="hidden peer" />
                                    <span className='peer-checked:bg-gradient-to-br from-secondary to-primary peer-checked:text-white peer-checked:font-medium text-center border border-gray rounded-xl w-full block px-2 py-2.5'>{translations?.female}</span>
                                </label>
                                <label htmlFor='other' className="flex-1 relative cursor-pointer">
                                    <Field type="checkbox" id="other" name="gender" value="other" className="hidden peer" />
                                    <span className='peer-checked:bg-gradient-to-br from-secondary to-primary peer-checked:text-white peer-checked:font-medium text-center border border-gray rounded-xl w-full block px-2 py-2.5'>{translations?.other}</span>
                                </label>
                            </div>
                            <ErrorMessage name="gender" component="div" className="text-red-500" />
                        </div> */}

                        {/* Platform Multi-Select */}
                        <div className='grid md:grid-cols-12 gap-3 items-center'>
                            <FormLabel customClass="col-span-3 !mb-0" title={translations?.platform} />
                            <div className='col-span-9'>
                                <Select
                                    isMulti
                                    name="platform"
                                    options={platformOptions}
                                    className="basic-multi-select"
                                    classNamePrefix="custom-select"
                                    placeholder={translations?.platformPlaceholder}
                                    value={values.platform}
                                    onChange={(selectedOptions) => {
                                        setFieldValue('platform', selectedOptions);
                                    }}
                                />
                            </div>
                        </div>

                        {/* Followers Range Slider */}
                        <div className='grid md:grid-cols-12 gap-3 items-center !mb-7'>
                            <FormLabel customClass="col-span-3 !mb-0" title={translations?.followers} />
                            <div className='col-span-9'>
                                <div className="flex items-center space-x-3">
                                    <div className='flex-1 relative'>
                                        <BorderInputWithCommas inputProps={{ type: 'text', name: 'minFollower', value: values.minFollower }} minFollowerValue={2000} maxFollowerValue={values.maxFollower} setFieldValue={setFieldValue} customClass="w-full flex-1 !py-2 !font-normal" />
                                        <span className='text-xs text-gray absolute top-1/2 right-4 -translate-y-1/2'>{translations?.min}</span>
                                    </div>
                                    <div className='flex-1 relative'>
                                        <BorderInputWithCommas inputProps={{ type: 'text', name: 'maxFollower', value: values.maxFollower }} minFollowerValue={values.minFollower} maxFollowerValue={1000000} customClass="w-full flex-1 !py-2 !font-normal" setFieldValue={setFieldValue} />
                                        <span className='text-xs text-gray absolute top-1/2 right-4 -translate-y-1/2'>{translations?.max}</span>
                                    </div>
                                </div>
                            </div>
                            <div className='col-span-3'></div>
                            <div className='col-span-9 mt-2'>
                                <ReactSlider
                                    className="w-full h-0.5 rounded-full"
                                    thumbClassName="h-4 w-4 bg-gradient-to-br from-secondary to-primary rounded-full cursor-pointer -mt-1.5"
                                    trackClassName="bg-gray h-0.5"
                                    min={0}
                                    max={1000000}
                                    step={100}
                                    value={[values.minFollower, values.maxFollower]}
                                    onChange={(value) => {
                                        setFieldValue('minFollower', value[0]);
                                        setFieldValue('maxFollower', value[1]);
                                    }}
                                    withTracks={true}
                                    renderTrack={(props, state) => {
                                        return (
                                            <div
                                                {...props}
                                                className={state.index === 1 ? "bg-gradient-to-br from-secondary to-primary h-0.5" : "bg-gray h-0.5"}
                                            />
                                        );
                                    }}
                                />
                            </div>
                        </div>

                        {/* Engagement Range Slider */}
                        <div className='grid md:grid-cols-12 gap-3 items-center !mb-7'>
                            <FormLabel customClass="col-span-3 !mb-0" title={translations?.engagement} />
                            <div className='col-span-9'>
                                <div className="flex items-center space-x-3">
                                    <div className='flex-1 relative'>
                                        <FormikInput inputProps={{ type: 'text', name: 'minEngagement', value: `${values.minEngagement}%`, readOnly: true }} customClass="w-full flex-1 !py-2 !font-normal" onChange={(e) => setFieldValue('minEngagement', parseFloat(e.target.value) || 0)} />
                                        <span className='text-xs text-gray absolute top-1/2 right-4 -translate-y-1/2'>{translations?.min}</span>
                                    </div>
                                    <div className='flex-1 relative'>
                                        <FormikInput inputProps={{ type: 'text', name: 'maxEngagement', value: `${values.maxEngagement}%`, readOnly: true }} customClass="w-full flex-1 !py-2 !font-normal" onChange={(e) => setFieldValue('maxEngagement', parseFloat(e.target.value) || 0)} />
                                        <span className='text-xs text-gray absolute top-1/2 right-4 -translate-y-1/2'>{translations?.max}</span>
                                    </div>
                                </div>
                            </div>
                            <div className='col-span-3'></div>
                            <div className='col-span-9 mt-2'>
                                <ReactSlider
                                    className="w-full h-0.5 rounded-full"
                                    thumbClassName="h-4 w-4 bg-gradient-to-br from-secondary to-primary rounded-full cursor-pointer -mt-1.5"
                                    trackClassName="bg-gray h-0.5"
                                    min={0}
                                    max={100}
                                    step={0.1}
                                    value={[values.minEngagement, values.maxEngagement]}
                                    onChange={(value) => {
                                        setFieldValue('minEngagement', value[0]);
                                        setFieldValue('maxEngagement', value[1]);
                                    }}
                                    withTracks={true}
                                    renderTrack={(props, state) => {
                                        return (
                                            <div
                                                {...props}
                                                className={state.index === 1 ? "bg-gradient-to-br from-secondary to-primary h-0.5" : "bg-gray h-0.5"}
                                            />
                                        );
                                    }}
                                />
                            </div>
                        </div>

                        {/* Topic Multi-Select */}
                        <div className='grid md:grid-cols-12 gap-3 items-center'>
                            <FormLabel customClass="col-span-3 !mb-0" title={translations?.topic} />
                            <div className='col-span-9'>
                                <Select
                                    isMulti
                                    name="topic"
                                    options={topicOptions}
                                    className="basic-multi-select"
                                    classNamePrefix="custom-select"
                                    placeholder={translations?.topicPlaceholder}
                                    menuPlacement="top"
                                    value={values.topic}
                                    // onChange={(selectedOptions) => {
                                    //     const skillValues: any = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
                                    //     setFieldValue('topic', selectedOptions);
                                    // }}
                                    onChange={(selectedOptions) => {
                                        setFieldValue('topic', selectedOptions);
                                    }}
                                />
                            </div>
                        </div>

                        <div className='grid md:grid-cols-12 gap-3 items-center'>
                            <FormLabel customClass="col-span-3 !mb-0" title={translations?.keywords} />
                            <div className='col-span-9'>
                                <Select
                                    isMulti
                                    isDisabled={matchedKeywords.length === 0}
                                    name="keyword"
                                    options={matchedKeywords}
                                    className="basic-multi-select"
                                    classNamePrefix="custom-select"
                                    placeholder={translations?.keywordPlaceholder}
                                    menuPlacement="top"
                                    value={values.keywords}
                                    onChange={(selectedOption) => {
                                        setFieldValue('keywords', selectedOption);
                                    }}
                                />
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className='flex justify-end'>
                            <button type="submit" className="text-blue-600 p-0 border-0 bg-none font-medium text-base">
                                {translations?.searchInfluencer}
                            </button>
                        </div>
                    </Form>
                )
            }
            }
        </Formik>
    )
}

export default SearchForm
